import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import StatCard from '../components/StatCard';
import TransactionItem from '../components/TransactionItem';
import CloudSyncIndicator from '../components/CloudSyncIndicator';
import { Plus, Receipt, DollarSign, TrendingUp, TrendingDown, Camera, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const isFreeLimitReached = user?.subscription_status === 'free' && dashboard?.receipts_this_month >= 20;

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-2">
            Welcome back, {user?.name}
          </h1>
          <p className="text-lg text-muted-foreground">
            Here's your financial overview for this month
          </p>
        </div>

        {/* Subscription Alert */}
        {isFreeLimitReached && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-start space-x-3"
            data-testid="limit-reached-alert"
          >
            <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-yellow-400 font-medium">Monthly limit reached</p>
              <p className="text-sm text-muted-foreground mt-1">
                You've used all 20 free scans. Upgrade to Pro for unlimited receipts.
              </p>
            </div>
            <button
              onClick={() => navigate('/subscription')}
              className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
            >
              Upgrade
            </button>
          </motion.div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard
            title="Total Income"
            value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dashboard?.total_income || 0)}
            icon={TrendingUp}
            gradient
          />
          <StatCard
            title="Total Expenses"
            value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dashboard?.total_expenses || 0)}
            icon={TrendingDown}
          />
          <StatCard
            title="Net Balance"
            value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dashboard?.net_balance || 0)}
            icon={DollarSign}
          />
        </div>

        {/* Receipt Counter */}
        <div className="mb-8 p-6 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider mb-2">
                Receipt Scans This Month
              </p>
              <p className="text-3xl font-mono font-bold">
                {dashboard?.receipts_this_month} / {dashboard?.receipts_limit === -1 ? '∞' : dashboard?.receipts_limit}
              </p>
            </div>
            <Receipt className="w-12 h-12 text-indigo-400" />
          </div>
          {user?.subscription_status !== 'active' && (
            <div className="mt-4">
              <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 h-full transition-all duration-500"
                  style={{ width: `${Math.min((dashboard?.receipts_this_month / 20) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Cloud Sync Status - Only for Premium Users */}
        {user?.subscription_status === 'active' && (
          <div className="mb-8">
            <CloudSyncIndicator />
          </div>
        )}

        {/* Recent Transactions */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold font-['Outfit']">Recent Transactions</h2>
            <button
              onClick={() => navigate('/transactions')}
              className="text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {dashboard?.recent_transactions?.length > 0 ? (
              dashboard.recent_transactions.map((transaction) => (
                <TransactionItem
                  key={transaction.id}
                  transaction={transaction}
                  onUpdate={fetchDashboard}
                  onDelete={fetchDashboard}
                />
              ))
            ) : (
              <div className="p-8 text-center text-muted-foreground bg-white/5 border border-white/10 rounded-xl">
                <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No transactions yet. Start by scanning a receipt!</p>
              </div>
            )}
          </div>
        </div>

        {/* Floating Action Buttons */}
        <div className="fixed bottom-8 right-8 flex flex-col space-y-4 z-40">
          <button
            onClick={() => navigate('/scan')}
            data-testid="fab-scan-receipt"
            disabled={isFreeLimitReached}
            className="w-16 h-16 bg-gradient-to-tr from-indigo-500 to-purple-500 rounded-full shadow-[0_0_40px_rgba(99,102,241,0.5)] flex items-center justify-center text-white hover:scale-110 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Camera className="w-7 h-7" />
          </button>
          <button
            onClick={() => navigate('/add-income')}
            data-testid="fab-add-income"
            className="w-16 h-16 bg-gradient-to-tr from-green-500 to-emerald-500 rounded-full shadow-[0_0_40px_rgba(34,197,94,0.5)] flex items-center justify-center text-white hover:scale-110 transition-transform"
          >
            <Plus className="w-7 h-7" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
